import React from 'react';

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {Delete as DeleteIcon} from '@mui/icons-material';
import {useTheme} from '@mui/material/styles';
import {useNavigate} from 'react-router-dom';
import {defineMessages, useIntl} from 'react-intl';
import {useMutation, useQuery, useQueryClient} from 'react-query';

import API, {Types, VehiclePaginationProps, useCsrfToken} from '@wheeebo/polaris-api';
import {useSnackbar} from '@wheeebo/polaris-snackbar';
import {
  ConfirmationDialog,
  CircleLoader,
  GroupPanel,
  LoadError,
  Head,
  SearchForm,
  SquareChip,
  TooltipButton,
  VehiclePanel,
  useErrorHandler,
} from '@wheeebo/polaris-ui';

const INITIAL_ITEM_COUNT = 25;

const msgs = defineMessages({
  buttonRegister: {
    id: 'pages.admin.DronListPage.button.register',
    defaultMessage: 'Register a vehicle',
  },
  headerTitle: {
    id: 'pages.admin.DronListPage.header.title',
    defaultMessage: 'Vehicle List',
  },
  headerSubTitle: {
    id: 'pages.admin.DronListPage.header.subTitle',
    defaultMessage:
      'This page lists vehicles owned by groups that you belong to. Each vehicle can belong to multiple groups.',
  },
  cellVehicleName: {
    id: 'pages.admin.DronListPage.cell.vehicleName',
    defaultMessage: 'Vehicle',
  },
  cellGroupName: {
    id: 'pages.admin.DronListPage.cell.groupName',
    defaultMessage: 'Owner Groups',
  },
  cellActivationStatus: {
    id: 'pages.admin.DronListPage.cell.activationStatus',
    defaultMessage: 'Activation Status',
  },
  errorNoVehicle: {
    id: 'pages.admin.GroupListPage.error.noVehicle',
    defaultMessage: 'vehicle not found',
  },
  dialogConfirmationTitle: {
    id: 'pages.admin.GroupListPage.dialog.confirmationTitle',
    defaultMessage: 'Confirm',
  },
  dialogDeleteConfirmationContent: {
    id: 'pages.admin.GroupListPage.dialog.deleteConfirmationContent',
    defaultMessage: 'Are you really going to delete this group?',
  },
  successVehicleDeleted: {
    id: 'pages.admin.GroupListPage.success.vehicleDeleted',
    defaultMessage: 'Vehicle deleted',
  },
});

interface RowProps {
  vehicle: Types.Vehicle;
  onDelete: (vehicleId: Types.UUID) => void;
}

function Row({vehicle, onDelete}: RowProps) {
  const navigate = useNavigate();
  return (
    <TableRow hover sx={{cursor: 'pointer'}} onClick={() => navigate(`/vehicles/${vehicle.id}`)}>
      <TableCell sx={{pl: 1 / 4}}>
        <VehiclePanel vehicle={vehicle} />
      </TableCell>
      <TableCell>
        <Stack gap={1}>
          {vehicle.groups?.map((e) => (
            <GroupPanel key={e.id} group={e.group} />
          ))}
        </Stack>
      </TableCell>
      <TableCell>
        <SquareChip
          label={vehicle.activationConfirmed ? 'Activated' : 'Not-activated'}
          color={vehicle.activationConfirmed ? 'primary' : 'error'}
        />
      </TableCell>
      <TableCell align="right">
        <TooltipButton
          message="Delete"
          icon={<DeleteIcon fontSize="small" />}
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            onDelete(vehicle.id);
          }}
        />
      </TableCell>
    </TableRow>
  );
}

function VehicleListPage(): JSX.Element {
  const intl = useIntl();
  const theme = useTheme();
  const isBigScreen = useMediaQuery(theme.breakpoints.up('sm'));
  const navigate = useNavigate();
  const {csrfToken} = useCsrfToken();
  const {showAndEmitJsErrorEvent, showError} = useErrorHandler();
  const {showSuccess} = useSnackbar();
  const queryClient = useQueryClient();
  const [page, setPage] = React.useState(0);
  const [selectedVehicleId, setSelectedVehicleId] = React.useState<Types.UUID>();
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [query, setQuery] = React.useState<string>();
  const options: VehiclePaginationProps = {
    limit: INITIAL_ITEM_COUNT,
    offset: page * INITIAL_ITEM_COUNT,
    query,
    includeInactivated: true,
    preload: ['groups'],
  };
  const {data, isError, isLoading} = useQuery(API.Vehicles.listKey(options), () => API.Vehicles.list(options));
  const handleDelete = (vehicleId: Types.UUID) => {
    setSelectedVehicleId(vehicleId);
    setDeleteDialogOpen(true);
  };
  const {mutate: deleteVehicle} = useMutation<unknown, unknown>(
    () => {
      setDeleteDialogOpen(false);
      if (!selectedVehicleId) {
        showError(new Error(intl.formatMessage(msgs.errorNoVehicle)));
        return Promise.reject({});
      }
      return API.Vehicles.delete_(csrfToken, selectedVehicleId);
    },
    {
      onSuccess: async () => {
        queryClient.invalidateQueries(API.Vehicles.CacheKey);
        showSuccess(intl.formatMessage(msgs.successVehicleDeleted));
      },
      onError: showAndEmitJsErrorEvent,
    },
  );
  return (
    <Grid container spacing={1}>
      <Head title="Vehicles" />
      <Grid item xs={12} sm={6}>
        <Card>
          <CardHeader
            title={intl.formatMessage(msgs.headerTitle)}
            subheader={intl.formatMessage(msgs.headerSubTitle)}
            action={
              <Button onClick={() => navigate('/vehicles/new')}>
                {isBigScreen ? 'Register a new vehicle' : 'Register'}
              </Button>
            }
          />
          <CardContent>
            <SearchForm onSearch={setQuery} />
            {isLoading ? <CircleLoader /> : null}
            {isError ? <LoadError /> : null}
            {data && data.total > 0 ? (
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{intl.formatMessage(msgs.cellVehicleName)}</TableCell>
                      <TableCell>{intl.formatMessage(msgs.cellGroupName)}</TableCell>
                      <TableCell>{intl.formatMessage(msgs.cellActivationStatus)}</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data?.items?.map((e: Types.Vehicle) => (
                      <Row key={e.id} vehicle={e} onDelete={handleDelete} />
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TablePagination
                        rowsPerPageOptions={[INITIAL_ITEM_COUNT]}
                        count={data?.total || 0}
                        rowsPerPage={INITIAL_ITEM_COUNT}
                        page={page}
                        SelectProps={{
                          inputProps: {'aria-label': 'rows per page'},
                        }}
                        onPageChange={(_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) =>
                          setPage(newPage)
                        }
                      />
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2">No record found.</Typography>
            )}
          </CardContent>
        </Card>
        <ConfirmationDialog
          open={deleteDialogOpen}
          title={intl.formatMessage(msgs.dialogConfirmationTitle)}
          description={intl.formatMessage(msgs.dialogDeleteConfirmationContent)}
          onCancel={() => setDeleteDialogOpen(false)}
          onConfirm={() => deleteVehicle()}
        />
      </Grid>
    </Grid>
  );
}

export default React.memo(VehicleListPage);
